import styled from '@emotion/styled';
import { percentageFormatter } from '@dnhb/formatters';
import { useSelector } from 'react-redux';
import { PrimaryButton } from 'shared/buttons';
import RoundedButton from 'shared/buttons/RoundedButton';
import { Checkbox, CurrencyInput, RoundedRadioButton, SelectInput } from 'shared/inputs';
import { OnOutsideClick } from "@dnhb/functional-component";
import useMedia from 'shared/hooks/useMedia';
import { LabelText, Info } from 'shared/components';
import CalculatorSvg from 'images/renteoverzicht/calculator.svg';

const Menu = styled.div(props => ({
  position: 'absolute',
  boxSizing: 'border-box',
  width: '288px',
  left: '0px',
  marginTop: '15px',
  padding: '16px',
  boxShadow: '0 0 8px rgba(0, 0, 0, 0.15)',
  borderRadius: '4px',
  zIndex: 3,
  background: props.theme.cardColor,
  color: props.theme.primaryFontColor,
  '> div:last-of-type': {
    marginTop: '16px',
  },
  '&::before': {
    content: "''",
    position: 'absolute',
    top: '-5px',
    left: '30%',
    width: '0px',
    height: '0px',
    borderStyle: 'solid',
    borderWidth: '12px 0px 0px 12px',
    borderColor: `${props.theme.cardColor} transparent transparent transparent`,
    filter: 'drop-shadow(1px -2px 2px rgba(0, 0, 0, 0.2))',
    transform: 'rotate(-45deg)',
    zIndex: 2,
  },
}));

const CustomLtvButton = styled(RoundedButton)(props => ({
  svg: {
    display: 'block',
    width: '16px',
    height: '16px',
    marginRight: '6px',
    fill: props.active ? props.theme.secondaryFontColor : props.theme.highlightColor,
  },
}));

const ButtonContainer = styled.div({
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  '> *': {
    marginRight: '12px',
    marginTop: '8px'
  }
});

const MarketValueForm = ({
  ltvDropdown,
  ltvDropdownOpener,
  calculateMortageHandler,
  updateSendHandler,
  onLtvChange,
  switchMobileCustomLtv,
}) => {
  const { input, output : { errors} } = useSelector(state => state);
  const { onMobile } = useMedia();
  const handleCurrencyChange = ({ target }) => updateSendHandler(`${target.name}`, target.value);

  return (
    <div>
      <LabelText css={{ margin: 0 }}>{onMobile ? 'Selecteer een tariefklasse' : 'Tariefklasse'}
        <Info title="Tariefklasse">
          De tariefklasse is gebaseerd op de verhouding tussen de hypotheek en de marktwaarde van de woning.
          Als je een woning hebt of gaat kopen met Nationale Hypotheek Garantie, val je in de tariefklasse NHG.
        </Info>
      </LabelText>

      {onMobile && (
        <div>
          <SelectInput
            options={[
              { label: 'NHG', value: 'nhg' },
              { label: '50%', value: 50 },
              { label: '60%', value: 60 },
              { label: '70%', value: 70 },
              { label: '80%', value: 80 },
              { label: '90%', value: 90 },
              { label: '100%', value: 100 },
            ]}
            value={input.nhg ? 'nhg' : input.ltv}
            onChange={e => (e.value === 'nhg' ? updateSendHandler('nhg', true) : onLtvChange(e.value))}
            disabled={input.mobileCustomLtv}
            placeholder="Tariefklasse"
          />
          <div css={{ marginTop: '16px', marginBottom: '24px' }}>
            <Checkbox
              label="Zelf de tariefklasse berekenen"
              name="martkwaarde"
              value={input.mobileCustomLtv}
              onChange={() => switchMobileCustomLtv(true)}
            />
          </div>
          {input.mobileCustomLtv && (
            <div css={{ margin: '24px 0' }}>
              <LabelText css={{ marginTop: '32px' }}>Hypotheeksom</LabelText>
              <CurrencyInput
                name="mortgageValue"
                placeholder="Hypotheeksom"
                onChange={handleCurrencyChange}
                errors={errors.mortgage}
                value={input.mortgageValue}
              />

              <LabelText css={{ marginTop: '32px' }}>Woningwaarde / Koopsom</LabelText>
              <CurrencyInput
                name="houseValue"
                placeholder="Woningwaarde / Koopsom"
                onChange={handleCurrencyChange}
                errors={errors.house_value}
                value={input.houseValue}
              />
            </div>
          )}
        </div>
      )}

      {!onMobile && (
        <ButtonContainer>
          <div>
            <CustomLtvButton
              onClick={e => {
                ltvDropdownOpener(e);
                if (ltvDropdown) {
                  calculateMortageHandler();
                }
              }}
              active={ltvDropdown || (input.customLtv === input.ltv && input.nhg === false)}
              css={{ width: '184px', fontWeight: '600!important' }}
            >
              <CalculatorSvg/>
              <div>
                {input.customLtv
                  ? `Tariefklasse: ${percentageFormatter(input.customLtv, 2)}`
                  : 'Bereken tariefklasse'}
              </div>
            </CustomLtvButton>
            {ltvDropdown && (
              <OnOutsideClick onOutsideClick={ltvDropdownOpener}>
                <Menu>
                  <LabelText>Bereken uw tariefklasse</LabelText>
                  <CurrencyInput
                    name="mortgageValue"
                    placeholder="Hypotheeksom"
                    onChange={handleCurrencyChange}
                    errors={errors.mortgage}
                    value={input.mortgageValue}
                  />
                  <CurrencyInput
                    name="houseValue"
                    value={input.houseValue}
                    placeholder="Woningwaarde / Koopsom"
                    onChange={handleCurrencyChange}
                    errors={errors.house_value}
                  />
                  <PrimaryButton css={{ marginTop: '16px' }} onClick={() => calculateMortageHandler()}>
                    Bereken
                  </PrimaryButton>
                </Menu>
              </OnOutsideClick>
            )}
          </div>
          <RoundedRadioButton
            name="ltv"
            value="nhg"
            checked={input.nhg === true}
            onChangeHandler={() => updateSendHandler('nhg', true)}
          >
            NHG
          </RoundedRadioButton>
          <RoundedRadioButton
            name="ltv"
            value="50"
            checked={input.ltv === 50 && input.nhg === false}
            onChangeHandler={() => onLtvChange('50')}
          >
            50%
          </RoundedRadioButton>
          <RoundedRadioButton
            name="ltv"
            value="60"
            checked={input.ltv === 60 && input.nhg === false}
            onChangeHandler={() => onLtvChange('60')}
          >
            60%
          </RoundedRadioButton>
          <RoundedRadioButton
            name="ltv"
            value="70"
            checked={input.ltv === 70 && input.nhg === false}
            onChangeHandler={() => onLtvChange('70')}
          >
            70%
          </RoundedRadioButton>
          <RoundedRadioButton
            name="ltv"
            value="80"
            checked={input.ltv === 80 && input.nhg === false}
            onChangeHandler={() => onLtvChange('80')}
          >
            80%
          </RoundedRadioButton>
          <RoundedRadioButton
            name="ltv"
            value="90"
            checked={input.ltv === 90 && input.nhg === false}
            onChangeHandler={() => onLtvChange('90')}
          >
            90%
          </RoundedRadioButton>
          <RoundedRadioButton
            name="ltv"
            value="100"
            checked={input.ltv === 100 && input.nhg === false}
            onChangeHandler={() => onLtvChange('100')}
          >
            100%
          </RoundedRadioButton>
        </ButtonContainer>
      )}
    </div>
  );
};

export default MarketValueForm;
